import * as QueryKeys from "data";
import { KeyboardArrowDown } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  createTheme,
  Popover,
  Skeleton,
  ThemeProvider,
  Typography,
  useTheme
} from "@mui/material";
import CardContent from "@mui/material/CardContent";
import { grey } from "@mui/material/colors";
import { UsageState } from "contexts/models";
import { useIdentity } from "contexts/identity-context";
import { DateRange } from "contexts/models";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import * as echarts from "echarts";
import ReactECharts from "echarts-for-react";
import {
  DateRange as MuiDateRange,
  DateRangePicker as MuiDateRangePicker
} from "mui-daterange-picker";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import useDarkMode from "use-dark-mode";
import { darkTheme } from "./dark";
import {
  durationToDateRange,
  EChartsOptions,
  extractEChartData,
  getDisplayTimeFormat,
  preparePreDefinedRanges,
  timePeriodFromDateRange
} from "./utils-usage";
import { getActiveOrganisation } from "contexts/identity-context";
import { fetchApiKeys, fetchUsage } from "data/queries";
import { useQuery } from "react-query";
import { resolveIdentityId } from "utils/identity";

//  TODO , ADD ERROR MESSAGES

export const UsageGraph = () => {
  echarts.registerTheme("darkTheme", darkTheme);
  const darkMode = useDarkMode(false);

  // useQuery
  const [identityState] = useIdentity();
  const isUser = !getActiveOrganisation(identityState);
  const identityId = resolveIdentityId(identityState, isUser);
  
  

  const [startTime, setStartime] = useState(dayjs().subtract(1, "week").unix() * 1000);
  const [endTime, setEndTime] = useState(dayjs().unix() * 1000);

  const [duration, setDuration] = useState<DateRange>(DateRange.WEEK)

  const usageQuery = useQuery(
    [QueryKeys.usageGraph, identityId],
    () => fetchUsage(isUser, identityState, startTime, endTime),
    {
      onSuccess: (queryData) => {
        setUsageGraph((prevState) => {
          return {
            ...prevState,
            startTime: startTime,
            endTime: endTime,
            usage: queryData,
            duration: duration,
          };
        });
      },
    }
  );

  const apiKeysQuery = useQuery([QueryKeys.apiKeys, identityId], () => fetchApiKeys(isUser, identityState))

  const isLoading = usageQuery.isLoading || apiKeysQuery.isLoading
  const isError = usageQuery.isError || apiKeysQuery.isError
  const isSuccess = usageQuery.isSuccess && apiKeysQuery.isSuccess

  useEffect(() => {
    usageQuery.refetch();
  }, [startTime, endTime]);

  const initialDailyUsageState: UsageState = {
    startTime: startTime,
    endTime: endTime,
    usage: usageQuery.data,
    duration: duration,
  };

  const [usageGraph, setUsageGraph] = useState(initialDailyUsageState);

  const displayTimeformat = getDisplayTimeFormat(usageGraph.duration);

  const [muiDateRange, setMuiDateRange] = useState<MuiDateRange>({
    startDate: new Date(timePeriodFromDateRange(DateRange.DAY)[0]),
    endDate: new Date(timePeriodFromDateRange(DateRange.DAY)[1]),
  });

  const [isCustomDateRange, setIsCustomDateRange] = useState(false);

  dayjs.extend(customParseFormat);
  dayjs.extend(advancedFormat);

  return (
    <>
      <Card
        style={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CardContent
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {isLoading && (
            <>
              <Skeleton variant="rectangular" width="100%" height="100px" />
              <br />
              <Skeleton variant="rectangular" width="100%" height="300px" />
            </>
          )}

          {isError && (
            <>
              <Alert severity="error">
                An error occurred while fetching the usage data.
              </Alert>
            </>
          )}

          {isSuccess && (
            <>
              <DateRangePicker
                usageGraph={usageGraph}
                setDuration={setDuration}
                muiDateRange={muiDateRange}
                setMuiDateRange={setMuiDateRange}
                isCustomDateRange={isCustomDateRange}
                setIsCustomDateRange={setIsCustomDateRange}
                setStartime={setStartime}
                setEndTime={setEndTime}
              />
              <ReactECharts
                option={EChartsOptions(
                  extractEChartData(usageGraph, apiKeysQuery, isLoading, isError),
                  displayTimeformat,
                  usageQuery.data.intervalHours,
                  isLoading,
                  isError,
                )}
                notMerge
                style={{ height: 400 }}
                theme={darkMode.value ? darkTheme : undefined}
              />
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export const DateRangePicker = (props: {
  usageGraph: UsageState;
  setDuration: Dispatch<SetStateAction<DateRange>>;
  muiDateRange: MuiDateRange;
  setMuiDateRange: Dispatch<SetStateAction<MuiDateRange>>;
  isCustomDateRange: boolean;
  setIsCustomDateRange: Dispatch<SetStateAction<boolean>>;
  setStartime: Dispatch<SetStateAction<number>>;
  setEndTime: Dispatch<SetStateAction<number>>;
}) => {

  const [open, setOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(!open);
  };
  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };
  const dfmt = "Do MMM, H:mm  A";

  const theme = useTheme();
  const customTheme = createTheme(theme, {
    palette: {
      primary: {
        light:
          theme.palette.mode === "dark"
            ? grey[900]
            : theme.palette.primary.light,
      },
    },
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        width: "100%",
        justifyContent: "space-between",
        alignItems: "center",
      }}>
      <Typography variant="body2">
        {`Date Range: ${props.isCustomDateRange
          ? "Custom Range -"
          : props.usageGraph.duration + " -"
          } ${dayjs(props.usageGraph.startTime).format(dfmt)} - ${dayjs(
            props.usageGraph.endTime
          ).format(dfmt)} (${props.usageGraph.usage.intervalHours
          }h interval)`}{" "}
      </Typography>
      <Box>
        <Button
          onClick={handleClick}
          variant="contained"
          endIcon={<KeyboardArrowDown />}
          sx={{ width: "223px", height: "42px" }}>
          Select Date Range
        </Button>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}>
          <ThemeProvider theme={customTheme}>
            <MuiDateRangePicker
              open={true}
              toggle={handleClose}
              definedRanges={preparePreDefinedRanges()}
              minDate={dayjs().subtract(90, "days").toDate()}
              onChange={(range: any) => {
                props.setMuiDateRange(() => {
                  const startDate = range.startDate
                    ? range.startDate
                    : new Date();
                  const endDate = range.endDate ? range.endDate : new Date();
                  props.setIsCustomDateRange(false);

                  if (!range.label) {
                    // Custom range
                    endDate.setDate(endDate.getDate() + 1);
                    endDate.setMinutes(endDate.getMinutes() - 1);
                    props.setIsCustomDateRange(true);
                  }

                  return {
                    startDate: startDate,
                    endDate: endDate,
                  };
                });

                props.setStartime(range.startDate?.getTime() || 0);
                props.setEndTime(range.endDate?.getTime() || 0);
                props.setDuration(durationToDateRange(range))

                handleClose();
              }}
            />
          </ThemeProvider>
        </Popover>
      </Box>
    </Box>
  );
};
